import { createSelector } from "@reduxjs/toolkit";
import { areIntervalsOverlapping } from "date-fns";
import isEqual from "lodash/isEqual";

import { initialPastAssessmentFilters } from "@/data/templates/templatesSlice";
import { ExamTiming, TaskFormat, TaskKind } from "@/generated/graphql";
import { isExam } from "@/ui/task";

import { RootState } from "../store";
import {
  Category,
  IPastAssessment,
  IPastAssessmentFilters,
  Subcategory,
  Template,
  TemplateFilter,
  templateFilters,
} from "./types";
import { findSelectedSubcategory } from "./utils";

export const selectCategories = (state: RootState) =>
  state.templates.categories;
export const selectSelectedFilter = (state: RootState): TemplateFilter =>
  state.templates.selectedFilter;

export const selectSelectedSubcategory = createSelector(
  [selectCategories],
  (categories): Subcategory | undefined =>
    findSelectedSubcategory(categories.data)
);

export const selectViewableTemplates = createSelector(
  [selectSelectedSubcategory, selectSelectedFilter],
  (selectedSubcategory, selectedFilter): Template[] => {
    if (!selectedSubcategory) return [];
    if (selectedFilter === "all") {
      return selectedSubcategory.templates;
    }
    return selectedSubcategory.templates.filter((t) =>
      t.matchesFilters.includes(selectedFilter)
    );
  }
);

export const selectSelectedCategory = createSelector(
  [selectCategories],
  (categories): Category | undefined =>
    categories.data.find((c) =>
      c.subcategories.some((sc) => sc.isSelected === true)
    )
);

export const selectAvailableFilters = createSelector(
  [selectSelectedSubcategory],
  (selectedSubcategory): TemplateFilter[] => {
    if (!selectedSubcategory) return ["all"];
    const availableTemplateFilters: TemplateFilter[] =
      selectedSubcategory.templates.flatMap((t) => t.matchesFilters);
    return templateFilters.reduce(
      (availableFiltersSoFar, currentFilter) => {
        if (availableTemplateFilters.includes(currentFilter)) {
          availableFiltersSoFar.push(currentFilter);
        }
        return availableFiltersSoFar;
      },
      ["all"] as TemplateFilter[]
    );
  }
);

export const selectTemplate = createSelector(
  [selectCategories, (_state, templateId?: string) => templateId],
  (categories, templateId): Template | undefined => {
    if (templateId == null || templateId === "") return undefined;
    return categories.data
      .flatMap((c) => c.subcategories)
      .flatMap((sc) => sc.templates)
      .find((t) => t.id === templateId);
  }
);

export const selectAllTemplates = createSelector(
  [selectCategories],
  (categories): Template[] => {
    return categories.data
      .flatMap((c) => c.subcategories)
      .flatMap((sc) => sc.templates);
  }
);

export const selectShowUiSkeletons = ({
  templates: {
    categories: { isLoading, hasLoaded },
  },
}: RootState): boolean => !hasLoaded || isLoading;

export const selectPastAssessmentFilters = ({
  templates: { pastAssessmentFilters },
}: RootState) => pastAssessmentFilters;

export const selectIsPastAssessmentFiltersInitial = createSelector(
  [
    selectPastAssessmentFilters,
    (_state: RootState, isMultiformat: boolean) => isMultiformat,
    (
      _state: RootState,
      _isMultiformat: boolean,
      isGroupAssessment: boolean
    ) => isGroupAssessment,
  ],
  (filters, isMultiformat, isGroupAssessment) =>
    isEqual(
      normalizeFiltersForDestination(filters, isMultiformat, isGroupAssessment),
      initialPastAssessmentFilters
    )
);

const normalizeFiltersForDestination = (
  filters: IPastAssessmentFilters,
  isMultiformat: boolean,
  isGroupAssessment: boolean
): IPastAssessmentFilters => {
  const normalized = { ...filters };

  if (normalized.assessmentType === "all") {
    delete normalized.assessmentType;
  }

  if (normalized.taskFormat === "all") {
    delete normalized.taskFormat;
  }

  if (normalized.instructionTypes?.length === 0) {
    delete normalized.instructionTypes;
  }

  if (isMultiformat) {
    delete normalized.assessmentType;
    delete normalized.taskFormat;
    delete normalized.instructionTypes;
    return normalized;
  }

  if (isGroupAssessment) {
    delete normalized.assessmentType;
    delete normalized.taskFormat;
    return normalized;
  }

  delete normalized.instructionTypes;
  return normalized;
};

export const selectFilterPastAssessments = createSelector(
  [
    selectPastAssessmentFilters,
    (_state: RootState, pastAssessments: IPastAssessment[]) => pastAssessments,
    (
      _state: RootState,
      _pastAssessments: IPastAssessment[],
      isMultiformat: boolean
    ) => isMultiformat,
    (
      _state: RootState,
      _pastAssessments: IPastAssessment[],
      _isMultiformat: boolean,
      isGroupAssessment: boolean
    ) => isGroupAssessment,
  ],
  (filters, pastAssessments, isMultiformat, isGroupAssessment) =>
    pastAssessments.flatMap((pastAssessment) =>
      matchesAllFilters(pastAssessment, filters, isMultiformat, isGroupAssessment)
        ? [pastAssessment]
        : []
    )
);

const matchesAllFilters = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters,
  isMultiformat: boolean,
  isGroupAssessment: boolean
): boolean => {
  if (
    !matchesByDate(pastAssessment, filters) ||
    !matchesByName(pastAssessment, filters) ||
    !matchesBySubject(pastAssessment, filters)
  ) {
    return false;
  }

  const isSourceMultiformat =
    pastAssessment.taskFormat === TaskFormat.Multiformat;

  // The destination's format drives the source list. Multi-format destinations
  // show only multi-format sources and hide the format/instruction-type
  // control.
  if (isMultiformat) {
    return isSourceMultiformat;
  }

  if (isGroupAssessment) {
    if (isSourceMultiformat) {
      return false;
    }
    return matchesByInstructionType(pastAssessment, filters);
  }

  return (
    matchesByAssessmentType(pastAssessment, filters) &&
    matchesByTaskFormat(pastAssessment, filters)
  );
};

const matchesByDate = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters
): boolean => {
  const [fromDate, toDate] = filters.dueDateRange ?? [null, null];

  if (fromDate == null && toDate == null) return true;

  const getDateRange = (): { start: Date; end: Date } | null => {
    if (isExam(pastAssessment.settings)) {
      const { examStartDate, examEndDate, examTiming } =
        pastAssessment.settings;

      // Live exams filter by start date only
      if (
        examTiming === ExamTiming.Live &&
        examStartDate != null &&
        examStartDate !== ""
      ) {
        const date = new Date(examStartDate);
        return { start: date, end: date };
      }

      // Other exams use the full date range
      if (
        examStartDate != null &&
        examStartDate !== "" &&
        examEndDate != null &&
        examEndDate !== ""
      ) {
        return { start: new Date(examStartDate), end: new Date(examEndDate) };
      }
    } else if (
      pastAssessment.settings.dueDate != null &&
      pastAssessment.settings.dueDate !== ""
    ) {
      const date = new Date(pastAssessment.settings.dueDate);
      return { start: date, end: date };
    }

    return null;
  };

  const dateRange = getDateRange();
  if (dateRange == null) return false;

  if (fromDate != null && toDate != null) {
    return areIntervalsOverlapping(dateRange, {
      start: new Date(fromDate),
      end: new Date(toDate),
    });
  }

  const time = dateRange.start.getTime();
  if (fromDate != null && time < fromDate) return false;
  if (toDate != null && time > toDate) return false;

  return true;
};

const matchesByName = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters
): boolean =>
  filters.assessmentNameQuery != null && filters.assessmentNameQuery !== ""
    ? pastAssessment.name
        .trim()
        .toLowerCase()
        .includes(filters.assessmentNameQuery.trim().toLowerCase())
    : true;

const matchesBySubject = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters
): boolean =>
  filters.subject != null ? pastAssessment.subject.id === filters.subject.id : true;

const matchesByAssessmentType = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters
): boolean => {
  if (filters.assessmentType == null) return true;
  if (filters.assessmentType === "all") return true;
  return filters.assessmentType === pastAssessment.settings.assessmentType;
};

const matchesByTaskFormat = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters
): boolean => {
  if (filters.taskFormat == null) return true;
  if (filters.taskFormat === "all") return true;
  return filters.taskFormat === pastAssessment.taskFormat;
};

const matchesByInstructionType = (
  pastAssessment: IPastAssessment,
  filters: IPastAssessmentFilters
): boolean => {
  const types = filters.instructionTypes;
  if (!types || types.length === 0) return true;

  const groupAssessmentEnabled =
    pastAssessment.settings.groupAssessmentEnabled === true;

  return types.some((type) => {
    switch (type) {
      case "group-work":
        return (
          groupAssessmentEnabled && pastAssessment.taskKind === TaskKind.Group
        );
      case "individual-work":
        return (
          groupAssessmentEnabled &&
          pastAssessment.taskKind === TaskKind.Individual
        );
      case "cadmus-classic":
        return (
          !groupAssessmentEnabled &&
          pastAssessment.taskFormat === TaskFormat.Classic
        );
    }
  });
};
